import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #requiresExplicitSize = false;

  #sizeExplicitlySelected = false;

  #changeHandler = (event) => this.variantChanged(event);

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.#changeHandler);
    this.addEventListener('wm:size-required', this.#handleSizeRequired);
    this.#resizeObserver.observe(this);
    this.#syncOptionAvailability();
    this.#configureRequiredSizeSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#changeHandler);
    this.#abortController?.abort();
    this.removeEventListener('wm:size-required', this.#handleSizeRequired);
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const optionFieldset = selectedOption.closest('fieldset[data-option-name]');
    const optionName = optionFieldset?.getAttribute('data-option-name');

    if (this.#requiresExplicitSize && optionName === 'size' && event.isTrusted) {
      this.#sizeExplicitlySelected = true;
      this.dataset.wmSizeSelected = 'true';
      this.querySelector('.wm-size-required-message')?.setAttribute('hidden', '');
    }

    this.#selectAvailableVariantForColor(event.target);
    this.#selectAvailableVariantForSize(event.target);
    this.#syncOptionAvailability();
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    if (this.#requiresExplicitSize && !this.#sizeExplicitlySelected) {
      queueMicrotask(() => {
        this.#clearVisualSizeSelection();
        this.#syncOptionAvailability();
        this.#applySizeSelectionGate();
      });
    }
  }

  /**
   * When a shopper changes color, keep the selection on a purchasable variant.
   * Some colors do not exist in every extended size, so preserving the old size
   * can otherwise leave the picker on an impossible combination. In that case,
   * select the first available size for the requested color before the section
   * update is requested.
   * @param {EventTarget | null} target
   */
  #selectAvailableVariantForColor(target) {
    this.#selectAvailableVariantForOption(target, 'color');
  }

  /**
   * When a shopper changes size, keep the requested size and move to the
   * first color that actually offers it if the old color does not. This makes
   * size and color equal, interconnected ways to choose a valid variant.
   * @param {EventTarget | null} target
   */
  #selectAvailableVariantForSize(target) {
    this.#selectAvailableVariantForOption(target, 'size');
  }

  /**
   * @returns {Array<{id: number | string, available: boolean, options: string[]}> | null}
   */
  #readVariantMatrix() {
    const matrixScript = this.querySelector('script[data-wm-variant-matrix]');
    if (!(matrixScript instanceof HTMLScriptElement) || !matrixScript.textContent) return null;

    try {
      const variants = JSON.parse(matrixScript.textContent);
      return Array.isArray(variants) ? variants : null;
    } catch (error) {
      console.error('Unable to read product variant matrix', error);
      return null;
    }
  }

  /** Requires an intentional size choice without forcing an internal size. */
  #configureRequiredSizeSelection() {
    if (
      this.dataset.templateProductMatch !== 'true' ||
      this.closest('product-card') ||
      this.closest('quick-add-dialog')
    ) {
      return;
    }

    const sizeFieldset = this.querySelector('fieldset[data-option-name="size"]');
    if (!(sizeFieldset instanceof HTMLFieldSetElement)) return;

    this.#requiresExplicitSize = true;
    this.dataset.wmSizeSelected = 'false';
    this.#clearVisualSizeSelection();
    this.#syncOptionAvailability();
    this.#applySizeSelectionGate();
  }

  /** Removes every visible size choice without changing the current variant ID. */
  #clearVisualSizeSelection() {
    const sizeFieldset = this.querySelector('fieldset[data-option-name="size"]');
    if (!(sizeFieldset instanceof HTMLFieldSetElement)) return;

    for (const input of sizeFieldset.querySelectorAll('input[type="radio"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = false;
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
    }

    const fieldsetIndex = Number.parseInt(sizeFieldset.dataset.fieldsetIndex || '', 10);
    if (!Number.isNaN(fieldsetIndex)) this.#checkedIndices[fieldsetIndex] = [];
  }

  /** Gives desktop shoppers clear feedback when Add to Cart needs a size. */
  #handleSizeRequired = () => {
    if (window.matchMedia('(max-width: 749px)').matches) return;

    const sizeFieldset = this.querySelector('fieldset[data-option-name="size"]');
    const message = sizeFieldset?.querySelector('.wm-size-required-message');
    if (!(sizeFieldset instanceof HTMLFieldSetElement) || !(message instanceof HTMLElement)) return;

    message.removeAttribute('hidden');
    sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /**
   * Records whether a deliberate size choice has been made. The purchase
   * buttons remain normal Add to Cart buttons; product-form opens the mobile
   * size prompt when a shopper tries to add without choosing.
   */
  #applySizeSelectionGate() {
    const requiresChoice = this.#requiresExplicitSize && !this.#sizeExplicitlySelected;
    this.dataset.wmSizeSelected = requiresChoice ? 'false' : 'true';

    if (requiresChoice) {
      const section = this.closest('.shopify-section');
      const stickySize = section?.querySelector(
        `sticky-add-to-cart[data-product-id="${this.dataset.productId}"] .sticky-add-to-cart__mobile-size`
      );
      if (stickySize) stickySize.textContent = 'Size';
    }
  }

  /**
   * Selects a size from the mobile sticky prompt as a deliberate shopper
   * choice. The exact locally resolved variant ID is returned immediately so
   * the cart request does not need to wait for the product section refresh.
   * @param {string} value
   * @returns {string | null}
   */
  selectSizeFromSticky(value) {
    if (!this.#requiresExplicitSize) return null;

    const sizeFieldset = this.querySelector('fieldset[data-option-name="size"]');
    if (!(sizeFieldset instanceof HTMLFieldSetElement)) return null;

    const sizeInput = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]')).find(
      (input) => input instanceof HTMLInputElement && input.value === value
    );
    if (!(sizeInput instanceof HTMLInputElement) || sizeInput.dataset.optionAvailable === 'false') return null;

    this.#sizeExplicitlySelected = true;
    this.dataset.wmSizeSelected = 'true';
    sizeInput.click();
    return sizeInput.dataset.variantId || null;
  }

  /** Resolve the shopper's current controls, never an asynchronously updated URL. */
  getSelectedVariantId() {
    const variants = this.#readVariantMatrix();
    if (!variants) return null;
    const options = [...this.querySelectorAll('fieldset[data-fieldset-index]')].map(
      (fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value || ''
    );
    if (!options.length || options.some((value) => !value)) return null;
    const variant = variants.find((candidate) => candidate.available &&
      candidate.options?.length === options.length &&
      candidate.options.every((value, index) => value === options[index]));
    return variant ? String(variant.id) : null;
  }

  /**
   * @param {EventTarget | null} target
   * @param {'color' | 'size'} optionName
   */
  #selectAvailableVariantForOption(target, optionName) {
    if (!(target instanceof HTMLInputElement)) return;

    const targetFieldset = target.closest(`fieldset[data-option-name="${optionName}"]`);
    if (!(targetFieldset instanceof HTMLFieldSetElement)) return;

    const variants = this.#readVariantMatrix();
    if (!variants) return;

    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    const selectedValues = fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });
    const exactVariant = variants.find(
      (variant) =>
        variant.available === true &&
        Array.isArray(variant.options) &&
        variant.options.every((value, index) => value === selectedValues[index])
    );

    if (exactVariant) {
      target.dataset.variantId = String(exactVariant.id);
      return;
    }

    const optionIndex = Number.parseInt(targetFieldset.dataset.fieldsetIndex || '', 10);
    if (Number.isNaN(optionIndex)) return;

    const fallbackVariant = variants.find(
      (variant) =>
        variant.available === true &&
        Array.isArray(variant.options) &&
        variant.options[optionIndex] === target.value
    );
    if (!fallbackVariant) return;

    fallbackVariant.options.forEach((optionValue, index) => {
      const fieldset = fieldsets[index];
      const input = fieldset
        ? Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (candidate) => candidate instanceof HTMLInputElement && candidate.value === optionValue
          )
        : undefined;

      if (input instanceof HTMLInputElement) this.updateSelectedOption(input);
    });

    target.dataset.variantId = String(fallbackVariant.id);
  }

  /**
   * Recalculate every option against the shopper's current choices. We keep
   * unavailable controls clickable so choosing one can move the other option
   * to a valid combination, while the visual and accessible state stays true.
   */
  #syncOptionAvailability() {
    const variants = this.#readVariantMatrix();
    if (!variants) return;

    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    const selectedValues = fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });

    fieldsets.forEach((fieldset, optionIndex) => {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

      inputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;

        const available = variants.some(
          (variant) =>
            variant.available === true &&
            Array.isArray(variant.options) &&
            variant.options[optionIndex] === input.value &&
            variant.options.every(
              (value, index) => index === optionIndex || selectedValues[index] === '' || value === selectedValues[index]
            )
        );

        input.dataset.optionAvailable = String(available);
        input.dataset.baseAriaLabel ||= input.value;

        if (available) {
          input.removeAttribute('aria-disabled');
          input.setAttribute('aria-label', input.dataset.baseAriaLabel);
        } else {
          input.setAttribute('aria-disabled', 'true');
          input.setAttribute('aria-label', `${input.dataset.baseAriaLabel} - Sold out`);
        }
      });
    });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const controller = this.#abortController;
    this.querySelector('[data-wm-variant-error]')?.remove();

    fetch(requestUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Product options could not be loaded');
        return response.text();
      })
      .then((responseText) => {
        if (controller.signal.aborted) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) throw new Error('Product option response is incomplete');

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }

        queueMicrotask(() => this.#applySizeSelectionGate());
      })
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        this.#pendingRequestUrl = undefined;
        const message = document.createElement('p');
        message.dataset.wmVariantError = '';
        message.setAttribute('role', 'alert');
        message.textContent = "We couldn't update this option. ";
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button-unstyled';
        retry.textContent = 'Try again';
        retry.addEventListener('click', () => {
          this.dispatchEvent(new VariantSelectedEvent({ id: this.selectedOptionId || '' }));
          this.fetchUpdatedSection(requestUrl, morphElementSelector);
        }, { once: true });
        message.append(retry);
        this.append(message);
        this.dispatchEvent(new CustomEvent('wm:variant-error', {
          bubbles: true,
          detail: { productId: this.dataset.productId },
        }));
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    if (this.#requiresExplicitSize && !this.#sizeExplicitlySelected) {
      this.#clearVisualSizeSelection();
    }

    this.#syncOptionAvailability();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
